import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouteSearch, useSetRouteSearch } from "./index";

/**
 * How one query parameter reads into a value and writes back out of one.
 *
 * `read` gets null for a missing parameter and must answer the default for
 * anything it does not recognise: a shared link can be old, hand-edited or
 * from another build, and the page has to open anyway. `write` answers null
 * for the default, so the address only ever spells out what differs from it.
 *
 * Methods rather than function properties: a schema mixes params of several
 * value types, and only method parameters are checked bivariantly, which is
 * what lets each one sit in a `Record<string, UrlParam<unknown>>`.
 */
export type UrlParam<T> = {
  read(raw: string | null): T;
  write(value: T): string | null;
};

export type UrlParamValues<S> = {
  [K in keyof S]: S[K] extends UrlParam<infer T> ? T : never;
};

export type UrlStateWrite = { replace?: boolean; viewTransition?: boolean };

/** Free text such as a search box. Empty is the default. */
export function textParam(): UrlParam<string> {
  return {
    read: (raw) => raw ?? "",
    write: (value) => (value === "" ? null : value),
  };
}

/** One of a fixed set of values, such as a tab, a sort or a status filter. */
export function enumParam<const T extends string>(
  values: readonly T[],
  fallback: T,
): UrlParam<T> {
  return {
    read: (raw) =>
      raw !== null && (values as readonly string[]).includes(raw)
        ? (raw as T)
        : fallback,
    write: (value) => (value === fallback ? null : value),
  };
}

/** A 1-based page number. */
export function pageParam(): UrlParam<number> {
  return numberParam(1, (value) => Number.isInteger(value) && value > 0);
}

/** A number the page accepts, such as a threshold from a fixed list. */
export function numberParam(
  fallback: number,
  isValid: (value: number) => boolean,
): UrlParam<number> {
  return {
    read: (raw) => {
      const value = raw === null || raw === "" ? Number.NaN : Number(raw);
      return Number.isFinite(value) && isValid(value) ? value : fallback;
    },
    write: (value) => (value === fallback ? null : String(value)),
  };
}

/** An on/off switch, written as 1 or 0 when it differs from its default. */
export function flagParam(fallback = false): UrlParam<boolean> {
  return {
    read: (raw) => (raw === "1" ? true : raw === "0" ? false : fallback),
    write: (value) => (value === fallback ? null : value ? "1" : "0"),
  };
}

/** The id of the item a page has open, or null when none is. */
export function idParam(): UrlParam<string | null> {
  return {
    read: (raw) => (raw ? raw : null),
    write: (value) => value || null,
  };
}

/**
 * A subset of a fixed set, such as the severities a list shows. Written in
 * the set's own order so one subset has one spelling; an empty subset is
 * written as an empty value, which is not the same as the default. A value
 * naming nothing the set knows reads as the default, not as empty.
 */
export function subsetParam<const T extends string>(
  values: readonly T[],
  fallback: readonly T[] = values,
): UrlParam<readonly T[]> {
  const canonical = (items: readonly string[]) =>
    values.filter((value) => items.includes(value));
  const fallbackKey = canonical(fallback).join(",");
  return {
    read: (raw) => {
      if (raw === null) return fallback;
      if (raw === "") return [];
      const items = canonical(raw.split(","));
      return items.length ? items : fallback;
    },
    write: (value) => {
      const key = canonical(value).join(",");
      return key === fallbackKey ? null : key;
    },
  };
}

/**
 * Ids the page has picked, such as several projects to compare. Empty is the
 * default. Ids are joined with commas, so an id holding one would split.
 */
export function idListParam(): UrlParam<readonly string[]> {
  return {
    read: (raw) => (raw ? raw.split(",").filter(Boolean) : []),
    write: (value) => (value.length ? value.join(",") : null),
  };
}

export function readUrlState<S extends Record<string, UrlParam<unknown>>>(
  schema: S,
  search: URLSearchParams,
): UrlParamValues<S> {
  return Object.fromEntries(
    Object.entries(schema).map(([name, param]) => [
      name,
      param.read(search.get(name)),
    ]),
  ) as UrlParamValues<S>;
}

/**
 * The address with `patch` written over it. Every other parameter is kept,
 * the scope the address names among them, and a value back at its default
 * drops its parameter.
 */
export function writeUrlState<S extends Record<string, UrlParam<unknown>>>(
  schema: S,
  search: URLSearchParams,
  patch: Partial<UrlParamValues<S>>,
): URLSearchParams {
  const next = new URLSearchParams(search);
  for (const [name, value] of Object.entries(patch)) {
    const raw = schema[name]?.write(value);
    if (raw === null || raw === undefined) next.delete(name);
    else next.set(name, raw);
  }
  return next;
}

/**
 * A page's view state, held in the address so a link, a reload and Back all
 * show the same view. Define the schema once at module level: its identity is
 * what keeps the values and the setter stable between renders.
 *
 * Pass `{ replace: true }` for refinements of the same view (typing, filters,
 * sort) so they are not each a Back step; leave it off for moving to another
 * view (a tab, an opened item, a page of results).
 */
export function useUrlState<S extends Record<string, UrlParam<unknown>>>(
  schema: S,
) {
  const search = useRouteSearch();
  const setRouteSearch = useSetRouteSearch();
  const key = search.toString();
  const values = useMemo(
    () => readUrlState(schema, new URLSearchParams(key)),
    [schema, key],
  );
  const update = useCallback(
    (patch: Partial<UrlParamValues<S>>, options?: UrlStateWrite) => {
      setRouteSearch((current) => {
        const next = writeUrlState(schema, current, patch);
        const address = next.toString();
        if (address !== current.toString()) {
          pageWrite = { address, revision: pageWrite.revision + 1 };
        }
        return next;
      }, options);
    },
    [schema, setRouteSearch],
  );
  return [values, update] as const;
}

const TYPING_SETTLE_MS = 250;
let pageWrite = { address: "", revision: 0 };

/**
 * A text box whose value lives in the address. The box answers each keystroke
 * from its own state, because the router applies an address change in a
 * transition and a box held only by that would drop characters and move the
 * caret; the address takes the text once typing pauses, replacing its entry
 * so a search is one Back step. When the address changes from elsewhere (Back,
 * a link) the box follows it.
 */
export function useUrlTextDraft(value: string, commit: (next: string) => void) {
  // The whole query, not just this text. A page restore keeps `value` and
  // must still drop a keystroke that has not landed.
  const address = useRouteSearch().toString();
  const [draft, setDraft] = useState(value);
  // An acknowledgement of our own write must preserve text typed since it was sent.
  const written = useRef<string | null>(null);
  const seenPageWrite = useRef(pageWrite.revision);
  const current = useRef(value);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => {
    current.current = value;
    const wasPageWrite =
      seenPageWrite.current !== pageWrite.revision &&
      address === pageWrite.address;
    seenPageWrite.current = pageWrite.revision;
    // The text we just sent coming back is ours, even when the write also
    // moved another param. Any other address change is navigation.
    if (value === written.current) written.current = null;
    else if (wasPageWrite) return;
    else {
      window.clearTimeout(timer.current);
      setDraft(value);
    }
  }, [address, value]);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const change = useCallback(
    (next: string) => {
      setDraft(next);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        if (next === current.current) return;
        written.current = next;
        commit(next);
      }, TYPING_SETTLE_MS);
    },
    [commit],
  );
  return [draft, change] as const;
}
